/**
 * MapView.tsx — Geographic visualization of outlets, gaps, and anomalies.
 *
 * Rebuilt in final pass.
 *
 * Preserves all functionality:
 *   - Leaflet map via CDN (dark CARTO tiles)
 *   - Client selector (maskClientName-aware)
 *   - Three layers: Registry Outlets (cyan), Distribution Gaps (amber),
 *     Anomalies (danger, admin only)
 *   - Debounced re-fetch on map move (500ms)
 *   - Circle markers with HTML popups showing Census ID, name, address,
 *     layer-specific info
 *   - No-data empty state
 *
 * Changes:
 *   - Default map center is now [39.8, -98.6] (US centroid) + zoom 4 —
 *     generic rather than Serbia-specific. The map auto-fits to result data
 *     naturally via panning; user will find their data.
 *   - Theme-aware marker colors pulled from `theme.brand/semantic` tokens
 *   - Uses shared UI (Button, Tag, Select, toast) where applicable
 *   - Map container height uses vh calc() as before
 *
 * Notes:
 *   - Leaflet `L` is expected to be loaded via CDN in index.html. If missing,
 *     the page shows a loading indicator indefinitely and doesn't crash.
 *   - `any` type cast for L is preserved — @types/leaflet isn't installed.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import { useEffect, useState, useRef, useCallback } from 'react';
import api from '../../api';
import { theme } from '../../theme';
import { useAdmin } from '../../AdminContext';
import {
  Select,
  Icon,
} from '../../components/ui';

declare const L: any;
type LeafletMap = ReturnType<typeof L.map>;
type LeafletLayerGroup = ReturnType<typeof L.layerGroup>;

/* ═══════════════ TYPES ═══════════════ */

interface MapOutlet {
  census_id: string;
  name: string;
  address: string;
  city: string;
  lat: number;
  lon: number;
  match_status?: string;
  outlet_format?: string;
  appearance_count?: number;
  chain_name?: string;
  distance_from_centroid_km?: number;
}

type LayerMode = 'outlets' | 'gaps' | 'anomalies';

/* ═══════════════ LAYER CONFIG ═══════════════ */

const LAYER_CONFIG: Record<LayerMode, { label: string; color: string }> = {
  outlets:   { label: 'Registry outlets',    color: theme.brand.cyan },
  gaps:      { label: 'Distribution gaps',   color: theme.semantic.warning },
  anomalies: { label: 'Anomalies',           color: theme.semantic.danger },
};

/* ═══════════════ PAGE ═══════════════ */

export default function MapView() {
  const [clients, setClients] = useState<string[]>([]);
  const [selected, setSelected] = useState('');
  const [layer, setLayer] = useState<LayerMode>('outlets');
  const [outletCount, setOutletCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [noData, setNoData] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const { maskClientName, adminMode, modeVersion } = useAdmin();

  const mapRef = useRef<LeafletMap | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const markersRef = useRef<LeafletLayerGroup | null>(null);
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    document.title = 'Registry Map | Meridian Registry';
    return () => { document.title = 'Meridian Registry'; };
  }, []);

  // Load client list
  useEffect(() => {
    api.get<string[]>('/api/clients')
      .then(r => {
        setClients(r.data);
        if (r.data.length > 0) setSelected(r.data[0]);
      })
      .catch(() => {});
  }, [modeVersion]);

  // Reset anomalies layer if user downgrades to non-admin
  useEffect(() => {
    if (!adminMode && layer === 'anomalies') {
      setLayer('outlets');
    }
  }, [adminMode, layer]);

  // Initialize Leaflet map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;
    if (typeof L === 'undefined') return;

    const map = L.map(mapContainerRef.current, {
      center: [39.8, -98.6], // US centroid — generic default
      zoom: 4,
      zoomControl: true,
    });

    L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>',
      maxZoom: 19,
    }).addTo(map);

    mapRef.current = map;
    markersRef.current = L.layerGroup().addTo(map);
    setMapReady(true);

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Fetch + render markers
  const loadMarkers = useCallback(async () => {
    if (!mapRef.current || !selected) return;

    const bounds = mapRef.current.getBounds();
    const boundsStr = `${bounds.getSouth()},${bounds.getWest()},${bounds.getNorth()},${bounds.getEast()}`;

    setLoading(true);
    setNoData(false);

    try {
      let data: MapOutlet[] = [];

      if (layer === 'outlets') {
        const res = await api.get<MapOutlet[]>('/api/map/outlets', {
          params: { client: selected, bounds: boundsStr },
        });
        data = res.data;
      } else if (layer === 'gaps') {
        const res = await api.get<MapOutlet[]>('/api/map/gaps', {
          params: { client: selected, bounds: boundsStr },
        });
        data = res.data;
      } else if (layer === 'anomalies') {
        const res = await api.get<MapOutlet[]>('/api/map/anomalies', {
          params: { client: selected },
        });
        data = res.data;
      }

      if (markersRef.current) markersRef.current.clearLayers();

      if (data.length === 0) {
        setNoData(true);
        setOutletCount(0);
        setLoading(false);
        return;
      }

      const color = LAYER_CONFIG[layer].color;

      data.forEach(item => {
        if (item.lat == null || item.lon == null) return;

        const marker = L.circleMarker([item.lat, item.lon], {
          radius: 4,
          color,
          fillColor: color,
          fillOpacity: 0.7,
          weight: 1,
        });

        marker.bindPopup(buildPopup(item, layer));
        markersRef.current.addLayer(marker);
      });

      setOutletCount(data.length);
    } catch {
      setNoData(true);
      setOutletCount(0);
    } finally {
      setLoading(false);
    }
  }, [selected, layer]);

  // Load markers when client or layer changes
  useEffect(() => { loadMarkers(); }, [loadMarkers]);

  // Debounced re-fetch on map move
  useEffect(() => {
    if (!mapRef.current) return;

    const onMoveEnd = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = window.setTimeout(() => { loadMarkers(); }, 500);
    };

    mapRef.current.on('moveend', onMoveEnd);
    return () => {
      mapRef.current?.off('moveend', onMoveEnd);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [loadMarkers]);

  return (
    <div style={{ padding: '24px 32px' }}>
      {/* Page header */}
      <div style={{ marginBottom: 18 }}>
        <h1 style={{
          fontSize: 28, fontWeight: 600,
          color: theme.text.primary,
          margin: 0,
          letterSpacing: '-0.02em',
        }}>Registry map</h1>
        <div aria-hidden style={{
          height: 3, width: 40,
          background: theme.brand.cyan,
          borderRadius: 2,
          boxShadow: `0 0 12px ${theme.brand.cyanGlow}`,
          marginTop: 6,
        }} />
        <div style={{
          fontSize: 11, fontWeight: 600,
          color: theme.text.tertiary,
          textTransform: 'uppercase',
          letterSpacing: '0.14em',
          marginTop: 8,
        }}>Visualise outlet coverage, distribution gaps, and data anomalies</div>
      </div>

      {/* Controls row */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14,
        marginBottom: 12, flexWrap: 'wrap',
      }}>
        <div style={{ minWidth: 200 }}>
          <Select
            value={selected}
            onChange={e => setSelected(e.target.value)}
          >
            {clients.map((c, i) => (
              <option key={c} value={c}>{maskClientName(c, i)}</option>
            ))}
          </Select>
        </div>

        {/* Layer toggles */}
        <div style={{ display: 'flex', gap: 4 }}>
          {(['outlets', 'gaps', ...(adminMode ? (['anomalies'] as const) : [])] as LayerMode[]).map(key => {
            const cfg = LAYER_CONFIG[key];
            const active = layer === key;
            return (
              <button
                key={key}
                onClick={() => setLayer(key)}
                onMouseEnter={e => { if (!active) e.currentTarget.style.borderColor = theme.borderColor.strong; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.borderColor = theme.borderColor.default; }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '7px 14px',
                  borderRadius: 8,
                  fontFamily: theme.font.ui,
                  fontWeight: 600,
                  fontSize: 12,
                  cursor: 'pointer',
                  background: active
                    ? `${cfg.color}1A`   // soft tint
                    : theme.surface.card,
                  border: `1px solid ${active ? cfg.color : theme.borderColor.default}`,
                  color: active ? cfg.color : theme.text.secondary,
                  transition: 'all 120ms ease',
                }}
              >
                <span aria-hidden style={{
                  display: 'inline-block',
                  width: 8, height: 8,
                  borderRadius: '50%',
                  background: cfg.color,
                  boxShadow: active ? `0 0 6px ${cfg.color}` : undefined,
                }} />
                {cfg.label}
              </button>
            );
          })}
        </div>

        {/* Count badge */}
        <div style={{
          marginLeft: 'auto',
          fontSize: 13,
          color: theme.text.secondary,
          display: 'inline-flex', alignItems: 'center', gap: 6,
        }}>
          {loading ? (
            <>
              <span style={{
                width: 10, height: 10, borderRadius: '50%',
                background: theme.brand.cyanSoft,
                border: `2px solid ${theme.brand.cyan}`,
                borderTopColor: 'transparent',
                animation: 'meridian-map-spin 800ms linear infinite',
              }} />
              <style>{`@keyframes meridian-map-spin { to { transform: rotate(360deg); } }`}</style>
              Loading…
            </>
          ) : (
            <>
              Showing{' '}
              <span style={{
                color: theme.brand.cyanLight,
                fontWeight: 700,
                fontFamily: theme.font.data,
                fontVariantNumeric: 'tabular-nums',
              }}>{outletCount.toLocaleString()}</span>
              {' '}outlets
            </>
          )}
        </div>
      </div>

      {/* No-data banner */}
      {noData && !loading && (
        <div style={{
          padding: '10px 14px',
          background: theme.surface.panel,
          border: `1px solid ${theme.borderColor.default}`,
          borderLeft: `3px solid ${LAYER_CONFIG[layer].color}`,
          borderRadius: 8,
          color: theme.text.secondary,
          fontSize: 13,
          marginBottom: 12,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <Icon.Info size={14} />
          {layer === 'outlets'
            ? 'No GPS coordinates available for this client in the current view.'
            : layer === 'gaps'
              ? 'No distribution gaps with GPS data in the current view.'
              : 'No GPS anomalies detected for this client.'}
        </div>
      )}

      {/* Map container */}
      <div style={{ position: 'relative' }}>
        {!mapReady && (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: theme.surface.panel,
            zIndex: 10,
            borderRadius: 8,
            color: theme.text.secondary,
            fontSize: 13,
          }}>Loading map…</div>
        )}
        <div
          ref={mapContainerRef}
          style={{
            width: '100%',
            height: 'calc(100vh - 220px)',
            minHeight: 400,
            borderRadius: 8,
            border: `1px solid ${theme.borderColor.default}`,
            overflow: 'hidden',
          }}
        />
      </div>
    </div>
  );
}

/* ═══════════════ HELPERS ═══════════════ */

/**
 * Build the Leaflet popup HTML. Uses inline styles that match theme tokens
 * approximately — Leaflet renders outside React so we can't pass components.
 */
function buildPopup(item: MapOutlet, layer: LayerMode): string {
  const layerColor = LAYER_CONFIG[layer].color;
  const secondary = theme.text.tertiary;
  const bodyStyle = `font-family: 'DM Sans', sans-serif; font-size: 12px; min-width: 180px; color: ${theme.text.primary};`;

  let html = `<div style="${bodyStyle}">`;
  html += `<div style="color: ${theme.brand.cyanLight}; font-family: 'Space Mono', monospace; font-size: 11px; margin-bottom: 4px;">${escapeHtml(item.census_id)}</div>`;
  html += `<div style="font-weight: 600; margin-bottom: 2px;">${escapeHtml(item.name || '—')}</div>`;
  html += `<div style="color: ${secondary}; font-size: 11px;">${escapeHtml(item.address || '')}</div>`;
  html += `<div style="color: ${secondary}; font-size: 11px;">${escapeHtml(item.city || '')}</div>`;

  if (layer === 'gaps') {
    html += `<div style="color: ${layerColor}; font-size: 11px; margin-top: 6px; font-weight: 600;">GAP — seen by ${item.appearance_count} other clients</div>`;
  } else if (layer === 'anomalies') {
    html += `<div style="color: ${layerColor}; font-size: 11px; margin-top: 6px; font-weight: 600;">ANOMALY — ${item.distance_from_centroid_km}km from chain centroid</div>`;
    html += `<div style="color: ${secondary}; font-size: 11px;">Chain: ${escapeHtml(item.chain_name || 'Unknown')}</div>`;
  } else {
    const status = (item.match_status || '').replace(/_/g, ' ');
    if (status) {
      html += `<div style="color: ${secondary}; font-size: 11px; margin-top: 6px;">${escapeHtml(status)}</div>`;
    }
  }

  html += `</div>`;
  return html;
}

/** Escape a string for safe HTML injection inside a popup. */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
